import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const DURATION = 320;
const EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

/**
 * Exclusive FAQ accordion for the `mf-faq` section. Opening one question closes
 * the others, and both directions animate the answer height. Without this module
 * the markup keeps working as plain `<details>` elements.
 *
 * @typedef {{ items: HTMLDetailsElement[] }} Refs
 * @extends {Component<Refs>}
 */
class MfFaqAccordion extends Component {
  /** @type {WeakMap<HTMLDetailsElement, Animation>} */
  #animations = new WeakMap();

  /**
   * Handles a click on a question summary.
   * @param {MouseEvent} event - Click event whose target is the `<summary>`.
   */
  toggle(event) {
    const details = event.target instanceof Element ? event.target.closest('details') : null;
    if (!(details instanceof HTMLDetailsElement)) return;

    event.preventDefault();

    if (details.open && !details.hasAttribute('data-closing')) {
      this.#close(details);
      return;
    }

    for (const other of this.refs.items ?? []) {
      if (other !== details && other.open) this.#close(other);
    }

    this.#open(details);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const item of this.refs.items ?? []) this.#animations.get(item)?.cancel();
  }

  /** @param {HTMLDetailsElement} details */
  #open(details) {
    const panel = this.#panel(details);
    const from = this.#currentHeight(details, panel);

    details.removeAttribute('data-closing');
    details.open = true;
    if (!panel) return;

    this.#animate(details, panel, from, panel.scrollHeight);
  }

  /** @param {HTMLDetailsElement} details */
  #close(details) {
    const panel = this.#panel(details);
    if (!panel) {
      details.open = false;
      return;
    }

    details.setAttribute('data-closing', '');
    const animation = this.#animate(details, panel, this.#currentHeight(details, panel), 0);

    const finish = () => {
      details.open = false;
      details.removeAttribute('data-closing');
    };

    if (animation) {
      animation.addEventListener('finish', finish, { once: true });
    } else {
      finish();
    }
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   * @param {number} from
   * @param {number} to
   * @returns {Animation | null}
   */
  #animate(details, panel, from, to) {
    this.#animations.get(details)?.cancel();
    if (prefersReducedMotion()) return null;

    const animation = panel.animate(
      [
        { height: `${from}px`, opacity: from > 0 ? 1 : 0 },
        { height: `${to}px`, opacity: to > 0 ? 1 : 0 },
      ],
      { duration: DURATION, easing: EASING }
    );
    this.#animations.set(details, animation);
    return animation;
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement | null} panel
   */
  #currentHeight(details, panel) {
    if (!details.open || !panel) return 0;
    return panel.getBoundingClientRect().height;
  }

  /** @param {HTMLDetailsElement} details */
  #panel(details) {
    const panel = details.querySelector('.mf-faq__panel');
    return panel instanceof HTMLElement ? panel : null;
  }
}

if (!customElements.get('mf-faq-accordion')) {
  customElements.define('mf-faq-accordion', MfFaqAccordion);
}
